import React, { useMemo } from "react";
import { useField, useFormikContext } from "formik";
import { CircleDollarSign } from "lucide-react";
import SectionCard from "../ui/SectionCard";
import Field from "../ui/Field";
import MetricsStrip, { formatCurrency } from "../ui/MetricsStrip";
import { usePermissions } from "../../../../../../Hooks/usePermissions";
import { MENU, PERMISSION } from "../../../../../../lib/permissions";

const MIN_MARGIN_PERCENT = 30;

const PriceField = ({ name, label, required, placeholder, readOnly }) => {
  const [, meta, helpers] = useField(name);

  const handleChange = (e) => {
    const raw = e.target.value;
    helpers.setValue(raw === "" ? "" : parseFloat(raw));
  };

  return (
    <Field
      label={label}
      required={required}
      htmlFor={name}
      error={meta.touched ? meta.error : null}
    >
      <div className="pf-input-group">
        <input
          id={name}
          name={name}
          type="number"
          step="0.01"
          min="0"
          value={meta.value ?? ""}
          onChange={handleChange}
          onBlur={() => helpers.setTouched(true)}
          onWheel={(e) => e.currentTarget.blur()}
          className="pf-input"
          placeholder={placeholder}
          aria-invalid={meta.touched && !!meta.error}
          readOnly={readOnly}
          disabled={readOnly}
        />
        <span className="pf-input-group__suffix">SAR</span>
      </div>
    </Field>
  );
};

const PricingSection = () => {
  const { values } = useFormikContext();
  const { hasMenuAccess, hasAction } = usePermissions();
  const canSeeCost = hasMenuAccess(MENU.PRODUCT_COSTS);
  const canEditCost = hasAction(MENU.PRODUCT_COSTS, PERMISSION.EDIT);
  const canSeeRegularPrice = hasMenuAccess(MENU.PRODUCT_REGULAR_PRICE);
  const canEditRegularPrice = hasAction(
    MENU.PRODUCT_REGULAR_PRICE,
    PERMISSION.EDIT,
  );
  const canSeeSalePrice = hasMenuAccess(MENU.PRODUCT_SALE_PRICE);
  const canEditSalePrice = hasAction(MENU.PRODUCT_SALE_PRICE, PERMISSION.EDIT);

  const preview = useMemo(() => {
    const cost = parseFloat(values.cost) || 0;
    const regular = parseFloat(values.regular_price) || 0;
    const sale = parseFloat(values.sale_price) || 0;
    const active = sale > 0 ? sale : regular;
    const profit = +(active - cost).toFixed(2);
    const margin = cost > 0 ? +((profit / cost) * 100).toFixed(2) : 0;
    return { active, profit, margin, hasValues: cost > 0 && active > 0 };
  }, [values.cost, values.regular_price, values.sale_price]);

  const visiblePriceCount =
    Number(canSeeCost) + Number(canSeeRegularPrice) + Number(canSeeSalePrice);
  const gridClass =
    visiblePriceCount >= 3
      ? "pf-grid pf-grid--3"
      : visiblePriceCount === 2
        ? "pf-grid pf-grid--2"
        : "pf-grid";

  const marginTone = !preview.hasValues
    ? "neutral"
    : preview.margin < MIN_MARGIN_PERCENT
    ? "danger"
    : "success";

  const metrics = canSeeCost
    ? [
        {
          key: "active",
          label: "Active Price",
          value: formatCurrency(preview.active) ?? "—",
        },
        {
          key: "profit",
          label: "Profit",
          value: formatCurrency(preview.profit) ?? "—",
        },
        {
          key: "margin",
          label: "Profit Margin",
          value: preview.hasValues ? `${preview.margin}%` : "—",
          tone: marginTone,
          meta: preview.hasValues
            ? preview.margin < MIN_MARGIN_PERCENT
              ? `Below the ${MIN_MARGIN_PERCENT}% minimum`
              : "Healthy margin"
            : `Minimum allowed is ${MIN_MARGIN_PERCENT}%`,
        },
      ]
    : [
        {
          key: "active",
          label: "Active Price",
          value: formatCurrency(preview.active) ?? "—",
        },
      ];

  return (
    <SectionCard
      icon={<CircleDollarSign size={16} />}
      title="Pricing"
      subtitle={
        canSeeCost
          ? "Cost, retail price, and optional sale price — all VAT-inclusive in SAR."
          : "Retail price and optional sale price — all VAT-inclusive in SAR."
      }
      id="pricing"
    >
      <div className={gridClass}>
        {canSeeCost && (
          <PriceField
            name="cost"
            label="Cost (Supplier)"
            required={canEditCost}
            placeholder="0.00"
            readOnly={!canEditCost}
          />
        )}
        {canSeeRegularPrice && (
          <PriceField
            name="regular_price"
            label="Regular Price"
            required={canEditRegularPrice}
            placeholder="0.00"
            readOnly={!canEditRegularPrice}
          />
        )}
        {canSeeSalePrice && (
          <PriceField
            name="sale_price"
            label="Sale Price"
            placeholder="Optional"
            readOnly={!canEditSalePrice}
          />
        )}
      </div>

      <MetricsStrip metrics={metrics} />
    </SectionCard>
  );
};

export default React.memo(PricingSection);
